import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isTwoFactorResponse, AuthResponse } from '../../services/api';
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon, TruckIcon, LifebuoyIcon } from '@heroicons/react/24/outline';
import ChangePasswordModal from './ChangePasswordModal';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState<number | null>(null);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  const { login, complete2FALogin } = useAuth();
  const navigate = useNavigate();

  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Auto-focus first OTP input when 2FA screen shows
  useEffect(() => {
    if (show2FA && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [show2FA]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtpDigits = [...otpDigits];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtpDigits[index + i] = digit;
        }
      });
      setOtpDigits(newOtpDigits);
      const nextIndex = Math.min(index + digits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
    } else {
      const newOtpDigits = [...otpDigits];
      newOtpDigits[index] = value.replace(/\D/g, '');
      setOtpDigits(newOtpDigits);
      if (value && index < 5) {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const navigateAfterLogin = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    switch (user.role_name) {
      case 'customer':
        navigate('/customer-dashboard');
        break;
      case 'rider':
        if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
          navigate('/pending-approval');
        } else {
          navigate('/rider-dashboard');
        }
        break;
      case 'merchant':
        if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
          navigate('/pending-approval');
        } else {
          navigate('/merchant-dashboard');
        }
        break;
      case 'admin':
        navigate('/admin-dashboard');
        break;
      default:
        navigate('/');
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const otpCode = otpDigits.join('');
    if (otpCode.length !== 6 || !twoFactorUserId) {
      setError('Please enter all 6 digits');
      setLoading(false);
      return;
    }

    try {
      const response = await complete2FALogin(twoFactorUserId, otpCode);
      
      // Check if password change is required
      if ((response as any)?.requires_password_change) {
        setShowChangePasswordModal(true);
        setLoading(false);
        return;
      }
      
      navigateAfterLogin();
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      setError(errorDetail || 'Invalid verification code. Please try again.');
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password);
      
      // Handle Remember Me
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      // Check if 2FA is required
      if (isTwoFactorResponse(response)) {
        setTwoFactorUserId(response.user_id);
        setShow2FA(true);
        setLoading(false);
        return;
      }
      
      // Check if password change is required
      if ((response as AuthResponse & { requires_password_change?: boolean })?.requires_password_change) {
        setShowChangePasswordModal(true);
        setLoading(false);
        return;
      }
      
      navigateAfterLogin();
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      
      // Improved error messages with specific guidance
      if (typeof errorDetail === 'object' && errorDetail.error === 'verification_required') {
        setError(
          '⚠️ Account Not Verified: Please check your email and phone for verification codes. If you didn\'t receive them, please register again.'
        );
      } else if (err.response?.status === 401) {
        setError(
          '🔒 Incorrect email or password. Please try again or reset your password below.'
        );
      } else if (err.response?.status === 404) {
        setError(
          '❌ Account not found. Please check your email or create a new account.'
        );
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError(
          '⏱️ Connection timeout. Please check your internet connection and try again.'
        );
      } else if (!navigator.onLine) {
        setError(
          '📡 No internet connection. Please check your network and try again.'
        );
      } else {
        setError(errorDetail?.message || errorDetail || '❌ Login failed. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 2FA Verification Screen
  if (show2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-sky-50 via-white to-blue-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-volexx-lg shadow-volexx-lg border border-gray-100 p-6 sm:p-8">
            <div className="text-center mb-6 sm:mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-volexx-blue-500 to-volexx-blue-600 flex items-center justify-center shadow-lg">
                  <ShieldCheckIcon className="h-10 w-10 text-white" />
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Two-Factor Authentication
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                We've sent a 6-digit verification code to your email. Enter it below to complete your login.
              </p>
            </div>
            
            <form className="space-y-5" onSubmit={handle2FASubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx-lg text-sm sm:text-base">
                  {error}
                </div>
              )}
              
              <div className="flex justify-center gap-2 sm:gap-3">
                {otpDigits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { otpInputRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-volexx-blue-500 focus:ring-2 focus:ring-volexx-blue-500 transition-all"
                  />
                ))}
              </div>
              
              <button
                type="submit"
                disabled={loading || otpDigits.some(d => !d)}
                className="btn-primary w-full py-3 text-base sm:text-lg font-semibold transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  'Verify Code'
                )}
              </button>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShow2FA(false);
                    setTwoFactorUserId(null);
                    setOtpDigits(['', '', '', '', '', '']);
                    setError('');
                  }}
                  className="text-sm text-gray-600 hover:text-volexx-blue-600 transition-colors"
                >
                  Back to login
                </button>
              </div>
            </form>
            
            <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Code expires in 10 minutes.</strong> Check your email inbox and spam folder if you don't see the code.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-sky-50 via-white to-blue-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-volexx-lg shadow-volexx-lg border border-gray-100 p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex justify-center mb-2">
              <img 
                src="/logo.png" 
                alt="Volexx Logistics" 
                className="h-40 sm:h-48 w-auto object-contain"
              />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Sign in to your account or{' '}
              <Link
                to="/register"
                className="font-semibold text-volexx-blue-500 hover:text-volexx-blue-600 transition-all duration-200 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-volexx-blue-600 after:transition-all after:duration-300 hover:after:w-full"
              >
                create a new account
              </Link>
            </p>
          </div>
          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx-lg text-sm sm:text-base">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="email-address" className="form-label">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="form-input transition-all duration-300 focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 focus:scale-[1.01]"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="form-label mb-0">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs sm:text-sm font-medium text-volexx-blue-500 hover:text-volexx-blue-600 transition-all duration-200 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-volexx-blue-600 after:transition-all after:duration-300 hover:after:w-full"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="form-input pr-10 transition-all duration-300 focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 focus:scale-[1.01]"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Remember my email
              </label>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            {/* Social Login Divider */}
            <div className="relative pt-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500 font-medium">Or continue with</span>
              </div>
            </div>

            {/* Social Login Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => window.location.href = '/auth/google/login'}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-volexx-lg shadow-sm bg-white hover:bg-gray-50 transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-sm font-medium text-gray-700">Continue with Google</span>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="flex flex-col items-center space-y-1">
                  <ShieldCheckIcon className="h-6 w-6 text-green-500" />
                  <p className="text-xs text-gray-600 font-medium">Secure</p>
                  <p className="text-xs text-gray-500">Encrypted</p>
                </div>
                <div className="flex flex-col items-center space-y-1">
                  <TruckIcon className="h-6 w-6 text-volexx-blue-500" />
                  <p className="text-xs text-gray-600 font-medium">10,000+</p>
                  <p className="text-xs text-gray-500">Deliveries</p>
                </div>
                <div className="flex flex-col items-center space-y-1">
                  <LifebuoyIcon className="h-6 w-6 text-volexx-orange-500" />
                  <p className="text-xs text-gray-600 font-medium">24/7</p>
                  <p className="text-xs text-gray-500">Support</p>
                </div>
              </div>
              <p className="text-center text-xs text-gray-500 mt-3">
                🔒 Your data is protected with bank-level encryption
              </p>
            </div>
          </form>
        </div>
      </div>
      
      {/* Password Change Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={() => {
          setShowChangePasswordModal(false);
          // Navigate to appropriate dashboard after password change
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          switch (user.role_name) {
            case 'customer':
              navigate('/customer-dashboard');
              break;
            case 'rider':
              if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
                navigate('/pending-approval');
              } else {
                navigate('/rider-dashboard');
              }
              break;
            case 'merchant':
              if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
                navigate('/pending-approval');
              } else {
                navigate('/merchant-dashboard');
              }
              break;
            case 'admin':
              navigate('/admin-dashboard');
              break;
            default:
              navigate('/');
          }
        }}
      />
    </div>
  );
};

export default Login;
